import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LabelList
} from 'recharts';
import { FiArrowLeft, FiFilter } from 'react-icons/fi';

const MainPaymentAnalytics = () => {
    const [yearlyData, setYearlyData] = useState([]);
    const [monthlyData, setMonthlyData] = useState([]);
    const [categoryData, setCategoryData] = useState([]);
    const [paymentList, setPaymentList] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [loading, setLoading] = useState({
        yearly: true,
        monthly: false,
        category: false,
        list: false
    });
    const [error, setError] = useState('');
    const [view, setView] = useState('yearly'); // yearly, monthly, category, list
    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedCategoryForList, setSelectedCategoryForList] = useState(null);

    // Fetch available categories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/main-dashboard/categories`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
                if (response.data.success) {
                    setCategories(response.data.data);
                }
            } catch (err) {
                console.error('Failed to fetch categories:', err);
            }
        };
        fetchCategories();
    }, []);

    // Fetch yearly payment data
    const fetchYearlyData = async (category = 'all') => {
        try {
            setLoading(prev => ({ ...prev, yearly: true }));
            setError('');
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/main-dashboard/analytics/yearly?category=${category}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                setYearlyData(response.data.data);
            }
        } catch (err) {
            setError(`Failed to fetch yearly data: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(prev => ({ ...prev, yearly: false }));
        }
    };

    // Fetch monthly data for a specific year
    const fetchMonthlyData = async (year, category = 'all') => {
        try {
            setLoading(prev => ({ ...prev, monthly: true }));
            setError('');
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/main-dashboard/analytics/monthly/${year}?category=${category}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                setMonthlyData(response.data.data);
            }
        } catch (err) {
            setError(`Failed to fetch monthly data: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(prev => ({ ...prev, monthly: false }));
        }
    };

    // Fetch category breakdown for a specific year and month
    const fetchCategoryBreakdown = async (year, month) => {
        try {
            setLoading(prev => ({ ...prev, category: true }));
            setError('');
            const token = localStorage.getItem('token');
            const monthNames = [
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"
            ];
            const monthNumber = monthNames.indexOf(month) + 1;
            
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/main-dashboard/analytics/category/${year}/${monthNumber}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                setCategoryData(response.data.data);
            }
        } catch (err) {
            setError(`Failed to fetch category breakdown: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(prev => ({ ...prev, category: false }));
        }
    };

    // Fetch payment list for a specific category, year, and month
    const fetchPaymentList = async (category, year, month) => {
        try {
            setLoading(prev => ({ ...prev, list: true }));
            setError('');
            const token = localStorage.getItem('token');
            const monthNames = [
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"
            ];
            const monthNumber = monthNames.indexOf(month) + 1;
            
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/main-dashboard/analytics/payments/${category}/${year}/${monthNumber}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                setPaymentList(response.data.data);
                setSelectedCategoryForList(response.data.category);
            }
        } catch (err) {
            console.error('Error fetching payment list:', err);
            setError(`Failed to fetch payment list: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(prev => ({ ...prev, list: false }));
        }
    };

    useEffect(() => {
        fetchYearlyData(selectedCategory);
    }, [selectedCategory]);

    const handleYearClick = async (year) => {
        setSelectedYear(year);
        if (selectedCategory === 'all') {
            setView('monthly');
            await fetchMonthlyData(year, 'all');
        } else {
            // If a specific category is selected, go directly to monthly view
            setView('monthly');
            await fetchMonthlyData(year, selectedCategory);
        }
    };

    const handleMonthClick = async (year, month) => {
        setSelectedMonth(month);
        if (selectedCategory === 'all') {
            // Show category breakdown
            setView('category');
            await fetchCategoryBreakdown(year, month);
        } else {
            // Go directly to payment list
            setView('list');
            await fetchPaymentList(selectedCategory, year, month);
        }
    };

    const handleCategoryClick = async (category) => {
        setView('list');
        await fetchPaymentList(category, selectedYear, selectedMonth);
    };

    const handleBack = () => {
        if (view === 'list') {
            if (selectedCategory === 'all') {
                setView('category');
            } else {
                setView('monthly');
            }
            setSelectedCategoryForList(null);
        } else if (view === 'category') {
            setView('monthly');
            setSelectedMonth(null);
        } else if (view === 'monthly') {
            setView('yearly');
            setSelectedYear(null);
        }
    };

    const handleCategoryFilterChange = (e) => {
        const newCategory = e.target.value;
        setSelectedCategory(newCategory);
        setView('yearly');
        setSelectedYear(null);
        setSelectedMonth(null);
        setSelectedCategoryForList(null);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-SG', {
            style: 'currency',
            currency: 'SGD'
        }).format(value);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-SG');
    };

    const renderYearlyChart = () => (
        <ResponsiveContainer width="100%" height={400}>
            <BarChart 
                data={yearlyData}
                margin={{ top: 40, right: 30, left: 20, bottom: 5 }}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis 
                    tickFormatter={(value) => formatCurrency(value)}
                    width={100}
                    domain={[0, 'auto']}
                />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar
                    dataKey="totalAmount"
                    fill="#8884d8"
                    name="Total Payments (SGD)"
                    onClick={(data) => handleYearClick(data.year)}
                    cursor="pointer"
                >
                    <LabelList
                        dataKey="totalAmount"
                        position="top"
                        formatter={(value) => formatCurrency(value)}
                        style={{ fill: '#666' }}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );

    const renderMonthlyChart = () => (
        <ResponsiveContainer width="100%" height={400}>
            <BarChart 
                data={monthlyData}
                margin={{ top: 40, right: 30, left: 20, bottom: 5 }}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis 
                    tickFormatter={(value) => formatCurrency(value)}
                    width={100}
                    domain={[0, 'auto']}
                />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar
                    dataKey="totalAmount"
                    fill="#82ca9d"
                    name="Monthly Payments (SGD)"
                    onClick={(data) => handleMonthClick(selectedYear, data.month)}
                    cursor="pointer"
                >
                    <LabelList
                        dataKey="totalAmount"
                        position="top"
                        formatter={(value) => formatCurrency(value)}
                        style={{ fill: '#666' }}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );

    const renderCategoryChart = () => (
        <ResponsiveContainer width="100%" height={400}>
            <BarChart 
                data={categoryData}
                margin={{ top: 40, right: 30, left: 20, bottom: 5 }}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis 
                    tickFormatter={(value) => formatCurrency(value)}
                    width={100}
                    domain={[0, 'auto']}
                />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar
                    dataKey="totalAmount"
                    fill="#ffc658"
                    name="Category Payments (SGD)"
                    onClick={(data) => handleCategoryClick(data.category)}
                    cursor="pointer"
                >
                    <LabelList
                        dataKey="totalAmount"
                        position="top"
                        formatter={(value) => formatCurrency(value)}
                        style={{ fill: '#666' }}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );

    const renderPaymentList = () => {
        // Render different table columns based on category
        const renderMemberInfo = (payment) => {
            if (selectedCategoryForList === 'SBF') {
                return (
                    <div>
                        <div className="font-medium">{payment.sbfAdsID?.sbfAdsID || 'N/A'}</div>
                        {(payment.sbfAdsID?.sbfQuote || payment.sbfAdsID?.sbfAdsName) && (
                            <div className="text-sm text-gray-500">
                                {payment.sbfAdsID?.sbfQuote}
                                {payment.sbfAdsID?.sbfQuote && payment.sbfAdsID?.sbfAdsName ? ' - ' : ''}
                                {payment.sbfAdsID?.sbfAdsName}
                            </div>
                        )}
                    </div>
                );
            } else if (selectedCategoryForList === 'Unblock') {
                return (
                    <div>
                        <div className="font-medium">{payment.unblockID?.unblockID || 'N/A'}</div>
                        {(payment.unblockID?.TGContactUsername || payment.unblockID?.unblockName) && (
                            <div className="text-sm text-gray-500">
                                {payment.unblockID?.TGContactUsername}
                                {payment.unblockID?.TGContactUsername && payment.unblockID?.unblockName ? ' - ' : ''}
                                {payment.unblockID?.unblockName}
                            </div>
                        )}
                    </div>
                );
            } else if (selectedCategoryForList === 'God') {
                // God payments: individual shows godMember, group/add_on shows groupName
                if ((payment.paymentType === 'individual' || payment.paymentType === 'misc_fees') && payment.godMember) {
                    // For individual: show fullName (spaID - godName - webID)
                    const fullName = payment.godMember.fullName || 
                                   `${payment.godMember.godID || 'N/A'} - ${payment.godMember.godName || 'N/A'} - ${payment.godMember.webID || 'N/A'}`;
                    const displayName = payment.paymentType === 'misc_fees'
                        ? `${fullName} (Misc Fees)`
                        : fullName;
                    return (
                        <div>
                            <div className="font-medium">{displayName}</div>
                        </div>
                    );
                } else if ((payment.paymentType === 'group' || payment.paymentType === 'add_on') && payment.godGroup) {
                    // For group: show groupName, for add_on: show groupName (add-on)
                    const displayName = payment.paymentType === 'add_on' 
                        ? `${payment.godGroup.groupName} (add-on)`
                        : payment.godGroup.groupName;
                    return (
                        <div>
                            <div className="font-medium">{displayName || 'N/A'}</div>
                        </div>
                    );
                }
                return <div className="text-gray-500">N/A</div>;
            } else if (selectedCategoryForList === 'SFRT') {
                // SFRT payments: show sfrtID and category
                return (
                    <div>
                        <div className="font-medium">{payment.sfrtID || 'N/A'}</div>
                        {payment.category && (
                            <div className="text-sm text-gray-500">
                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    payment.category === 'SFT' ? 'bg-orange-100 text-orange-800' :
                                    payment.category === 'SRT' ? 'bg-pink-100 text-pink-800' :
                                    'bg-gray-100 text-gray-800'
                                }`}>
                                    {payment.category}
                                </span>
                            </div>
                        )}
                    </div>
                );
            } else if (selectedCategoryForList === 'SIMON') {
                // SIMON receivables: show supplier and payment period
                return (
                    <div>
                        <div className="font-medium">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                payment.supplier === 'Bean' ? 'bg-yellow-100 text-yellow-800' :
                                payment.supplier === 'Doraemon' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                            }`}>
                                {payment.supplier || 'N/A'}
                            </span>
                        </div>
                        {payment.paymentForDateFrom && payment.paymentForDateTo && (
                            <div className="text-sm text-gray-500 mt-1">
                                {formatDate(payment.paymentForDateFrom)} - {formatDate(payment.paymentForDateTo)}
                            </div>
                        )}
                    </div>
                );
            } else if (selectedCategoryForList === 'GZ') {
                // FO orders: show customerID - Name
                return (
                    <div>
                        {payment.customer ? (
                            <>
                                <div className="font-medium">
                                    {payment.customer.customerID || 'N/A'}
                                    {payment.customer.Name ? ` - ${payment.customer.Name}` : ''}
                                </div>
                            </>
                        ) : 'N/A'}
                    </div>
                );
            } else if (selectedCategoryForList === 'Superman') {
                if (payment.SupermanMember) {
                    const m = payment.SupermanMember;
                    return (
                        <div>
                            <div className="font-medium">Superman: {m.name || '—'}</div>
                            <div className="text-sm text-gray-500">{m.webID || 'no webID'}</div>
                        </div>
                    );
                }
                if (payment.godMember) {
                    const m = payment.godMember;
                    return (
                        <div>
                            <div className="font-medium">
                                God: {[m.godID, m.godName, m.webID].filter(Boolean).join(' - ') || '—'}
                            </div>
                        </div>
                    );
                }
                return <div className="text-gray-500">N/A</div>;
            }
            return 'N/A';
        };

        return (
            <div className="mt-4">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Payment Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Member
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Currency
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Amount (SGD)
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Renewal Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Remarks
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {paymentList.length > 0 ? (
                                paymentList.map((payment) => (
                                    <tr key={payment._id}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {formatDate(payment.paymentDate)}
                                        </td>
                                        <td className="px-6 py-4">
                                            {renderMemberInfo(payment)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {selectedCategoryForList === 'God' || selectedCategoryForList === 'Superman' || selectedCategoryForList === 'GZ' ? 'SGD' : (payment.currency || 'N/A')}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap font-medium text-green-600">
                                            {formatCurrency(
                                                selectedCategoryForList === 'God' || selectedCategoryForList === 'Superman' ? payment.paymentAmount :
                                                selectedCategoryForList === 'GZ' ? (payment.amountSGD || payment.totalProfit || 0) :
                                                (payment.amountSGD || 0)
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {selectedCategoryForList === 'SFRT' || selectedCategoryForList === 'SIMON' || selectedCategoryForList === 'GZ' || selectedCategoryForList === 'Superman' ? 'N/A' : formatDate(payment.renewalDate)}
                                        </td>
                                        <td className="px-6 py-4">
                                            {selectedCategoryForList === 'God' || selectedCategoryForList === 'Superman' ? (payment.paymentRemarks || '-') :
                                             selectedCategoryForList === 'GZ' ? 'N/A' :
                                             (payment.remarks || '-')}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                                        No payments found for this period
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderLoading = () => (
        <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
    );

    // Calculate total amount across all years
    const totalAmount = yearlyData.reduce((sum, year) => sum + (year.totalAmount || 0), 0);

    return (
        <div className="bg-white rounded-lg shadow w-full">
            <div className="flex items-center justify-between mb-6 px-8 py-8">
                <div className="flex items-center gap-4">
                    {view !== 'yearly' && (
                        <button
                            onClick={handleBack}
                            className="flex items-center text-blue-600 hover:text-blue-800"
                        >
                            <FiArrowLeft className="mr-2" />
                            Back
                        </button>
                    )}
                    <h2 className="text-xl font-semibold">
                        {selectedCategory === 'all' ? 'Combined' : (categories.find((c) => c.key === selectedCategory)?.name || selectedCategory)} Payment Analytics{' '}
                        <span style={{ color: 'red' }}>({formatCurrency(totalAmount)})</span>
                    </h2>
                </div>
                
                {/* Category Filter Dropdown */}
                <div className="flex items-center gap-2">
                    <FiFilter className="text-gray-600" />
                    <select
                        value={selectedCategory}
                        onChange={handleCategoryFilterChange}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                        <option value="all">All Categories</option>
                        {categories.map(cat => (
                            <option key={cat.key} value={cat.key}>{cat.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md mx-8">
                    {error}
                </div>
            )}

            {view === 'yearly' && (loading.yearly ? renderLoading() : renderYearlyChart())}
            {view === 'monthly' && (
                <>
                    <h3 className="text-lg font-medium mb-4 px-8">
                        Monthly Payments for {selectedYear}{' '}
                        <span className="text-red-600">
                            ({formatCurrency(monthlyData.reduce((sum, month) => sum + (month.totalAmount || 0), 0))})
                        </span>
                    </h3>
                    {loading.monthly ? renderLoading() : renderMonthlyChart()}
                </>
            )}
            {view === 'category' && (
                <>
                    <h3 className="text-lg font-medium mb-4 px-8">
                        Category Breakdown for {selectedMonth} {selectedYear}{' '}
                        <span className="text-red-600">
                            ({formatCurrency(categoryData.reduce((sum, cat) => sum + (cat.totalAmount || 0), 0))})
                        </span>
                    </h3>
                    {loading.category ? renderLoading() : renderCategoryChart()}
                </>
            )}
            {view === 'list' && (
                <>
                    <h3 className="text-lg font-medium mb-4 px-8">
                        {selectedCategoryForList} Payments for {selectedMonth} {selectedYear}{' '}
                        <span className="text-red-600">
                            ({formatCurrency(paymentList.reduce((sum, payment) => {
                                const amount = selectedCategoryForList === 'God' || selectedCategoryForList === 'Superman'
                                    ? (payment.paymentAmount || 0)
                                    : selectedCategoryForList === 'GZ'
                                        ? (payment.amountSGD || payment.totalProfit || 0)
                                        : (payment.amountSGD || 0);
                                return sum + amount;
                            }, 0))})
                        </span>
                    </h3>
                    {loading.list ? renderLoading() : renderPaymentList()}
                </>
            )}
        </div>
    );
};

export default MainPaymentAnalytics;
